/**
 * ComboBox с idField
 */
import { extractUniDataRowColumnValue } from '@/components/ui/table/formatters'
import { isListboxCatalogLikeScalar, sanitizeListboxScalarValue } from '@/common/utils/listboxScalarSanitize'
import { listboxOptionValueFromRow } from '@/common/utils/listboxTableResolve'
import { setType } from '@/common/utils/typedValueSerializer'
import { normalizeFormElementType } from '@/common/utils/formElementType'
import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'

export type ComboBoxIdFieldPair = { outKey: string; listboxColumn: string }

function readIdFieldItemOutKey(i: Record<string, any>): string {
  const fromNested =
    i.id?._text ?? i.id?._TEXT ?? (typeof i.id === 'string' ? i.id : '') ?? ''
  const attrs = i._attributes && typeof i._attributes === 'object' ? (i._attributes as Record<string, unknown>) : {}
  const fromAttrs =
    (typeof attrs.id === 'string' ? attrs.id : '') ||
    (typeof attrs.Id === 'string' ? attrs.Id : '') ||
    ''
  return String(fromNested || fromAttrs).trim()
}

function readIdFieldItemListboxColumn(i: Record<string, any>): string {
  const fromNested =
    i.id__listbox?._text ??
    i.id__listbox?._TEXT ??
    (typeof i.id__listbox === 'string' ? i.id__listbox : '') ??
    ''
  const attrs = i._attributes && typeof i._attributes === 'object' ? (i._attributes as Record<string, unknown>) : {}
  const fromAttrs =
    (typeof attrs.id__listbox === 'string' ? attrs.id__listbox : '') ||
    (typeof attrs['id_Listbox'] === 'string' ? attrs['id_Listbox'] : '') ||
    ''
  return String(fromNested || fromAttrs).trim()
}

export function parseComboBoxIdFieldPairsFromElement(elementLike: any): ComboBoxIdFieldPair[] {
  if (!elementLike || typeof elementLike !== 'object') return []
  const raw = elementLike.idField
  if (!raw || typeof raw !== 'object') return []
  const itemsRaw = (raw as { item?: unknown }).item ?? (raw as { Item?: unknown }).Item
  const items = Array.isArray(itemsRaw) ? itemsRaw : itemsRaw ? [itemsRaw] : []
  const out: ComboBoxIdFieldPair[] = []
  for (const item of items) {
    if (!item || typeof item !== 'object') continue
    const i = item as Record<string, any>
    const outKey = readIdFieldItemOutKey(i)
    const listboxColumn = readIdFieldItemListboxColumn(i)
    if (outKey && listboxColumn) {
      out.push({ outKey, listboxColumn })
    }
  }
  return out
}

export function getListboxRowsFromPageFieldEntry(listboxField: any): any[] | undefined {
  if (listboxField == null) return undefined
  const unwrap =
    listboxField &&
    typeof listboxField === 'object' &&
    !Array.isArray(listboxField) &&
    'value' in listboxField
      ? (listboxField as { value: unknown }).value
      : listboxField
  const rows = (unwrap as { rows?: unknown })?.rows
  return Array.isArray(rows) ? rows : undefined
}

export function findListboxRowMatchingSelectedValue(rows: any[], selectedId: string): any | undefined {
  const sid = String(selectedId)
  return rows.find((row) => String(listboxOptionValueFromRow(row)) === sid)
}

const LISTBOX_VALUE_COLUMNS = ['code', 'kind', 'value', '_id'] as const

function readListboxRowFieldEntry(row: any, column: string): { type?: string; value?: unknown } | undefined {
  if (!row || typeof row !== 'object' || !column) return undefined
  const fields = (row as { fields?: Record<string, unknown> }).fields
  if (!fields || typeof fields !== 'object') return undefined
  const direct = fields[column]
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct as { type?: string; value?: unknown }
  }
  return undefined
}

/**
 * TypedValue из строки __listbox с сохранением type/value (SwingComboBox.getData → getField из row).
 */
export function extractTypedFieldFromListboxRow(
  row: any,
  column: string,
): { value: unknown; type: string } {
  const entry = readListboxRowFieldEntry(row, column)
  if (entry && 'value' in entry) {
    const v = entry.value
    if (v === undefined || v === null) {
      return { value: '', type: 'String' }
    }
    if (typeof entry.type === 'string' && entry.type.trim() !== '') {
      return { value: v, type: entry.type }
    }
    return setType(v)
  }

  const v = extractUniDataRowColumnValue(row, column)
  if (v === undefined || v === null) {
    return { value: '', type: 'String' }
  }
  return setType(v)
}

export function resolveListboxSelectionTypedValue(row: any): { value: unknown; type: string } {
  for (const column of LISTBOX_VALUE_COLUMNS) {
    const typed = extractTypedFieldFromListboxRow(row, column)
    if (typed.value !== '' && typed.value !== undefined && typed.value !== null) {
      return typed
    }
  }
  return { value: '', type: 'String' }
}

function readTypedFieldEntry(entry: unknown): { value: unknown; type: string } | null {
  if (entry == null) return null
  if (typeof entry === 'object' && !Array.isArray(entry) && 'type' in entry && 'value' in entry) {
    return {
      value: (entry as { value: unknown }).value,
      type: String((entry as { type?: unknown }).type ?? 'String'),
    }
  }
  return { value: entry, type: 'String' }
}

function isListboxCatalogFieldEntry(entry: unknown): boolean {
  const typed = readTypedFieldEntry(entry)
  if (!typed) return false
  if (typed.type === 'UniDataTable' || typed.type === 'UniDataTableFields') {
    return true
  }
  const v = typed.value
  return Boolean(v && typeof v === 'object' && !Array.isArray(v) && Array.isArray((v as { rows?: unknown }).rows))
}

function isEmptyListboxSelectionValue(value: unknown): boolean {
  const unwrapped = unwrapScriptValue(value)
  if (unwrapped == null) return true
  if (typeof unwrapped === 'string' && unwrapped.trim() === '') return true
  if (typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
    const text = String(unwrapped).trim()
    if (text === '') return true
  }
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

const listboxScalarDefaultLong = (): { value: number; type: string } => ({
  type: 'Long',
  value: -1,
})

function listboxScalarDefaultFromRows(rows: any[]): { value: unknown; type: string } {
  if (!rows.length) {
    return listboxScalarDefaultLong()
  }
  const sample = resolveListboxSelectionTypedValue(rows[0])
  if (sample.type === 'Boolean') {
    return { type: 'Boolean', value: false }
  }
  if (sample.type === 'String') {
    return sample
  }
  if (sample.type === 'Long' || sample.type === 'Integer' || sample.type === 'Double') {
    return sample
  }
  return sample
}

function readSanitizedListboxSelection(baseEntry: unknown): { value: unknown; type: string } | null {
  const selEntry = readTypedFieldEntry(baseEntry)
  if (!selEntry) return null
  if (isListboxCatalogLikeScalar(selEntry.value) || isListboxCatalogFieldEntry(baseEntry)) {
    return null
  }
  const sanitized = sanitizeListboxScalarValue(selEntry.value)
  if (isEmptyListboxSelectionValue(sanitized)) {
    return null
  }
  return { value: sanitized, type: selEntry.type }
}

/**
 * ListBox/ComboBox: если есть `{name}__listbox` и выбранная строка — подставляет type/value из row (напр. Long code),
 * а не String из UI. Нужно для storage.get("user", ctx.getField("dchUserId", -1)).
 * Пустой каталог / UniDataTable на имени поля → default по типу code в каталоге (String '', Boolean false, Long -1).
 */
export function applyListboxWireTypesFromListboxFields(clientDataFields: Record<string, any>): void {
  for (const key of Object.keys(clientDataFields)) {
    if (!key.endsWith('__listbox')) continue

    const baseName = key.slice(0, -'__listbox'.length)
    if (!baseName) continue

    const baseEntry = clientDataFields[baseName]
    const rows = getListboxRowsFromPageFieldEntry(clientDataFields[key]) ?? []
    const defaultScalar = () => listboxScalarDefaultFromRows(rows)

    if (isListboxCatalogFieldEntry(baseEntry)) {
      clientDataFields[baseName] = defaultScalar()
      continue
    }

    const sel = readSanitizedListboxSelection(baseEntry)
    if (!sel) {
      clientDataFields[baseName] = defaultScalar()
      continue
    }

    if (rows.length === 0) {
      clientDataFields[baseName] = defaultScalar()
      continue
    }

    const selectedId = String(sel.value ?? '')
    const selectedItem = findListboxRowMatchingSelectedValue(rows, selectedId)
    if (!selectedItem) {
      clientDataFields[baseName] = defaultScalar()
      continue
    }

    clientDataFields[baseName] = resolveListboxSelectionTypedValue(selectedItem)
  }
}

/**
 * Карта полей Swing для ComboBox с idField (по текущему значению и строкам __listbox).
 */
export function buildSwingComboBoxFieldMapFromMergedData(
  elementFieldName: string,
  elementXml: any,
  getMergedEntry: (fieldKey: string) => any,
): Record<string, { value: unknown; type: string }> | null {
  const pairs = parseComboBoxIdFieldPairsFromElement(elementXml)
  if (!pairs.length) return null

  const lb = getMergedEntry(`${elementFieldName}__listbox`)
  const rows = getListboxRowsFromPageFieldEntry(lb)
  const selField = getMergedEntry(elementFieldName)
  const rawSel =
    selField &&
    typeof selField === 'object' &&
    !Array.isArray(selField) &&
    'value' in selField
      ? (selField as { value: unknown }).value
      : selField
  const selectedId = String(rawSel ?? '')
  const selectedItem =
    Array.isArray(rows) && rows.length
      ? findListboxRowMatchingSelectedValue(rows, selectedId)
      : undefined

  const out: Record<string, { value: unknown; type: string }> = {}
  if (selectedItem) {
    for (const { outKey, listboxColumn } of pairs) {
      out[outKey] = extractTypedFieldFromListboxRow(selectedItem, listboxColumn)
    }
  } else {
    for (const { outKey } of pairs) {
      out[outKey] = { value: '', type: 'String' }
    }
  }
  return out
}

/**
 * Нормализует clientDataFields перед отправкой (executeScript / onClick): заполняет поля idField,
 * удаляет elementName__text.
 */
export function applyComboBoxSwingToClientDataFieldsFromRegistry(
  clientDataFields: Record<string, any>,
  mergedFlatParentData: Record<string, any>,
  elementsRegistry: Record<string, any> | null | undefined,
): void {
  if (!elementsRegistry || typeof elementsRegistry !== 'object') return

  for (const [fieldName, reg] of Object.entries(elementsRegistry)) {
    const elType = String(reg?.elementType ?? normalizeFormElementType(reg) ?? '').toUpperCase()
    if (elType !== 'COMBOBOX') continue

    const swing = buildSwingComboBoxFieldMapFromMergedData(fieldName, reg, (k) => {
      const fromClient = clientDataFields[k]
      if (fromClient !== undefined) return fromClient
      return mergedFlatParentData[k]
    })
    if (!swing) continue

    for (const [outKey, typed] of Object.entries(swing)) {
      clientDataFields[outKey] = typed
    }
    delete clientDataFields[`${fieldName}__text`]
  }
}

type UdLite = {
  hasField: (name: string) => boolean
  getField: (name: string) => any
}

/**
 * Если ComboBox с idField — возвращает TypedValue как в Swing; иначе undefined 
 */
export function trySwingComboBoxGetField(
  entity: any,
  ud: UdLite,
  base: string,
  sub?: string,
): ReturnType<typeof setType> | undefined {
  const elType = String(entity?.elementType ?? normalizeFormElementType(entity) ?? '').toUpperCase()
  if (elType !== 'COMBOBOX') return undefined

  const pairs = parseComboBoxIdFieldPairsFromElement(entity)
  if (!pairs.length) return undefined

  const swing = buildSwingComboBoxFieldMapFromMergedData(base, entity, (k) =>
    ud.hasField(k) ? ud.getField(k) : undefined,
  )
  if (!swing) return undefined

  if (!sub || sub === '') {
    const typed = (swing as Record<string, { value: unknown; type: string }>)[base]
    return typed ?? setType('')
  }

  if (sub.startsWith('__')) {
    const full = `${base}${sub}`
    if (full === `${base}__text`) {
      return setType('')
    }
    return ud.hasField(full) ? ud.getField(full) : { value: undefined, type: 'String' }
  }

  if (Object.prototype.hasOwnProperty.call(swing, String(sub))) {
    return (swing as Record<string, { value: unknown; type: string }>)[String(sub)] ?? setType('')
  }

  if (sub && ud.hasField(sub)) {
    return ud.getField(sub)
  }

  const legacy = `${base}__${sub}`
  return ud.hasField(legacy) ? ud.getField(legacy) : { value: undefined, type: 'String' }
}
